import type { Membership } from '../auth/api'
import type { Role } from '../lib/roles'
import type { ClientAccount } from './account'

/**
 * A company the signed-in user works with, and the ad accounts under it.
 *
 * The wireframe uses the word the other way round: its `clientDemo` string renders as
 * "Demo client" and labels the company carrying no accounts. Here the populated one is
 * the fixture client and `clientDemo` is not reused
 * (design: docs/plans/2026-09-18-gro-20-client-account-panels-design.md).
 */
export interface Client {
  id: string
  /** Display name. For the first-run client this is what the user typed at step 2. */
  name: string
  accounts: ClientAccount[]
  /**
   * Whether this client's figures are demo data, shown so a signed-in user has something
   * to explore before any real account can be connected.
   *
   * Presentation only. It decides where the demo marker and the demo notice appear and
   * nothing else: no client-side flag establishes account access, and the server is what
   * recognises account provenance (GRO-39).
   */
  isDemo: boolean
  /** The signed-in user's role in this company, joined from the session's memberships. */
  role: Role
}

/**
 * Joins the companies list (names, accounts) with the session's memberships (roles) by
 * company id. A company with no membership row, or a membership with no company, is
 * dropped: the two reads disagree only across a membership change, and neither half is
 * enough to render the company (GRO-106 design, "Session and roles").
 */
export function joinClients<C extends { id: string }>(
  companies: C[],
  memberships: Membership[],
  toClient: (company: C, role: Role) => Client,
): Client[] {
  const roles = new Map(memberships.map((m) => [m.companyId, m.role]))
  return companies.flatMap((company) => {
    const role = roles.get(company.id)
    return role === undefined ? [] : [toClient(company, role)]
  })
}

/** A name as the duplicate rule compares it: trimmed, without case (GRO-130, D4). */
export function foldName(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * The `Dict` key standing in for a client whose name is an empty string.
 * The data layer has no language, so it names the key and the caller resolves it.
 */
export const UNNAMED_CLIENT_KEY = 'clientUnnamed'

/**
 * Which client is active: the saved id when it names a client still in the list, else the
 * first (oldest) company.
 *
 * Called only from `App`'s render path, never while `FirstRun` is on screen, so `clients`
 * is guaranteed non-empty every time this runs (GRO-39: `list_for_user` orders by
 * `companies.created_at`, and the boundary never mounts `App` on an empty list).
 */
export function resolveClient(clients: Client[], storedId: string | null): Client {
  if (storedId !== null) {
    const saved = clients.find((c) => c.id === storedId)
    if (saved) return saved
  }
  return clients[0]
}
